import { Button, ChevronDownIcon, Popover } from '@iheartjane/reefer';
import every from 'lodash-es/every';
import type { ReactElement } from 'react';
import { cloneElement } from 'react';

import { useProductSearchFilters } from '@jane/bloom/hooks/filters';
import { FilterCategoryLabel } from '@jane/bloom/hooks/filters/useProductSearchFilters';
import type { FilterTypeKeys } from '@jane/bloom/viewModels/productSearchFilters';

import styles from './filterButtonWrapper.module.css';

interface FilterButtonWrapperProps {
  children: ReactElement;
  filterKey: FilterTypeKeys;
}

export function FilterButtonWrapper({
  children,
  filterKey,
}: FilterButtonWrapperProps) {
  const {
    getProductSearchFilterOptions,
    getProductSubcategories,
    selectedCategory,
  } = useProductSearchFilters();

  const getFilterOptions = (filterKey: FilterTypeKeys) => {
    if (filterKey === 'categories') {
      if (selectedCategory) {
        return getProductSubcategories(selectedCategory);
      } else {
        return getProductSearchFilterOptions('categories');
      }
    }

    if (filterKey === 'potencies') {
      return [
        ...getProductSearchFilterOptions('thcPotencies'),
        ...getProductSearchFilterOptions('cbdPotencies'),
      ];
    }

    return getProductSearchFilterOptions(filterKey);
  };

  const noProductsForAllOptions = (filterKey: FilterTypeKeys) => {
    return every(getFilterOptions(filterKey), ({ count }) => count === 0);
  };

  const filterOptionCount = getFilterOptions(filterKey).length;

  const filterLabel =
    filterKey === 'categories' && selectedCategory
      ? 'Subcategory'
      : FilterCategoryLabel[filterKey];

  return filterOptionCount > 0 ? (
    <Popover
      openOn={noProductsForAllOptions(filterKey) ? 'disabled' : 'click'}
      target={
        <Button
          className={styles['chip']}
          disabled={noProductsForAllOptions(filterKey)}
          variant="tertiary"
          endIcon={<ChevronDownIcon size="sm" />}
          label={filterLabel}
        />
      }
      label={FilterCategoryLabel[filterKey]}
      alignment={{ horizontal: 'left' }}
    >
      {({ closePopover }) => (
        <Popover.Content
          padding={false}
          className={styles['popoverContent']}
          label="filter-content"
        >
          {cloneElement(children, { closePopover })}
        </Popover.Content>
      )}
    </Popover>
  ) : null;
}
